import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import ParticleField from "@/components/ParticleField";
import { useContent } from "@/context/ContentContext";

export default function Founder() {
  const { content } = useContent();
  const bio = (content.founder_bio || "").split("\n\n");
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: content.founder_name || "Jove Divinity",
    jobTitle: "Founder & CEO",
    worksFor: { "@type": "Organization", name: "ImJove Tech" },
  };

  return (
    <div>
      <SEO title="Jove Divinity — Founder & CEO of ImJove Tech" description="Jove Divinity is the Founder and CEO of ImJove Tech, building an ecosystem of technology capable of shaping what comes next." image={content.founder_portrait} jsonLd={jsonLd} />
      <section className="relative overflow-hidden pt-32">
        <ParticleField className="absolute inset-0 z-0" density={30} />
        <div className="relative z-10 mx-auto grid max-w-[1400px] items-center gap-14 px-6 pb-24 md:px-12 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-6 rounded-3xl bg-gold/10 blur-3xl" />
              <img
                src={content.founder_portrait}
                alt="Jove Divinity"
                data-testid="founder-portrait"
                className="relative aspect-[4/5] w-full rounded-2xl border border-white/10 object-cover object-top grayscale-[15%]"
              />
              <div className="absolute bottom-5 left-5 glass rounded-xl px-4 py-3">
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-gold-light">Founder + CEO</p>
                <p className="font-display text-lg font-semibold text-white">Jove Divinity</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono2 text-xs uppercase tracking-brand text-gold-light">{content.founder_role || "Founder + CEO"}</p>
            <h1 className="mt-4 font-display text-6xl font-bold tracking-tighter text-white sm:text-7xl">{content.founder_name || "Jove Divinity"}</h1>
            <p className="mt-3 font-display text-xl font-light text-silver-gradient">{content.founder_subheadline || "Creator. Technologist. Visionary."}</p>
            <div className="mt-8 space-y-5">
              {bio.map((para, i) => (
                <p key={i} className="font-body leading-relaxed text-white/60">{para}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-ink-800 py-28">
        <div className="mx-auto max-w-[1100px] px-6 text-center md:px-12">
          <Reveal>
            <p className="font-display text-3xl font-light leading-snug text-white sm:text-4xl">
              "The future isn't something we're waiting on. <span className="text-gold-gradient">It's something we're building.</span>"
            </p>
            <p className="mt-6 font-mono2 text-xs uppercase tracking-widest text-white/40">— Jove Divinity</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { t: "Current Companies", d: "NitePass, ChefPlated, Jove AI, SM Genie and the growing ImJove ecosystem.", to: "/technology" },
              { t: "Future Innovations", d: "Jove Bodhi, Jove Infinity, Vyra and experimental technology from ImJove Labs.", to: "/labs" },
              { t: "The Vision", d: "Building an ecosystem of technology capable of shaping what comes next.", to: "/mission" },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <Link to={c.to} className="card-hover block h-full rounded-2xl border border-white/10 bg-ink-800 p-8">
                  <h3 className="font-display text-2xl font-semibold text-white">{c.t}</h3>
                  <p className="mt-3 font-body text-white/55">{c.d}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-gold-light">Explore <ArrowRight size={13} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
